import React, { Fragment } from "react";
import {
  ADD_RECEIVER_INFO,
  ADD_ITEM_DETAILS,
  ADD_PAYMENT_INFO,
  ADD_SHIPMENT_DETAILS,
  optionalCostList,
  paymentInfoOptions,
} from "constants/global";
import edit from "../../../../asset/images/edit-2.svg";
import truck from "asset/images/truckIcon.svg";
import ship from "asset/images/shipIcon.svg";
import getInitials from "utilities/getInitials";
import capitalizeFirstLetter from "utilities/capitalizeFirstLetter";
import currencyPairCheck from "utilities/currencyPairCheck";
import { Bars } from "react-loader-spinner";
import { useSelector } from "react-redux";

const ShipmentSummary = ({
  goToStep,
  firstStepDetails,
  shippingLegs,
  paymentDetails: paymentDetailsData,
  selectedCurrency,
  totalAmount,
  paymentTerm,
  selectedSupplier,
  shipmentSummary,
  file,
  submitPaymentDetails,
  isLoading,
  additionalCost,
  isEditing,
  isReceiver,
}) => {
  const {
    containerType,
    loadType,
    packageType,
    quantity,
    weight,
    length,
    width,
    height,
    document: firstFileDocument,
  } = firstStepDetails;
  const userDetails = useSelector((state) => state);
  const isAgent = {
    name: userDetails?.users?.firstName + " " + userDetails?.users?.lastName,
    email: userDetails?.users?.email,
    businessName: userDetails?.business?.name,
    business: { name: userDetails?.business?.name },
  };

  const shipmentWithData = {};
  for (const key in shipmentSummary) {
    if (shipmentSummary[key]) {
      shipmentWithData[key] = shipmentSummary[key];
    }
  }

  const paymentDetails = [
    {
      title: "Total Shipping Cost",
      fee: `${currencyPairCheck(selectedCurrency?.name)}${totalAmount ? totalAmount : 0}`,
      isBold: true,
    },
    {
      title: "Trucking Fee",
      fee: `${currencyPairCheck(selectedCurrency?.name)}${paymentDetailsData?.truckingFee ? paymentDetailsData?.truckingFee : 0
        }`,
      isBold: false,
    },
    {
      title: "Clearing Fee",
      fee: `${currencyPairCheck(selectedCurrency?.name)}${paymentDetailsData?.clearingFee ? paymentDetailsData?.clearingFee : 0
        }`,
      isBold: false,
    },
  ];

  for (const { value, label } of optionalCostList) {
    const data = additionalCost.find((cost) => cost?.feeType === value);
    if (data?.additionalCost) {
      paymentDetails.push({
        title: label,
        fee: `${currencyPairCheck(selectedCurrency?.name)}${data?.additionalCost ? data?.additionalCost : 0
          }`,
        isBold: false,
      });
    } else {
      continue;
    }
  }

  paymentDetails.push({
    title: "Payment Terms",
    fee: paymentInfoOptions.find((payment) => payment?.value === paymentTerm)?.label,
    isBold: true,
  });

  const shippersDocumentsList = shippingLegs.map((item) => item.document).filter(Boolean);

  const groupedShippers = shippingLegs.reduce((acc, curr) => {
    if (curr.isClearingAgent) {
      curr.clearingAgent = isAgent
    }
    if (!acc[curr.clearingAgent?.name]) {
      acc[curr.clearingAgent?.name] = curr.clearingAgent;
    }
    acc[curr.clearingAgent?.name] = curr?.clearingAgent;
    return acc;
  }, {});
  const shippingLines = shippingLegs?.map((item) => item.shippingLine).filter(Boolean);


  return (
    <>
      <form onSubmit={(evt) => evt.preventDefault()} className="">
        <div>
          <h6
            style={{
              color: "#101828",
              fontWeight: "500",
              fontSize: "24px",
              lineHeight: "32.4px",
            }}
          >
            Summary
          </h6>
          <p className="text-sm font-light text-[#475467]">
            Please review your shipment details before you continue
          </p>
        </div>

        <div className="flex flex-col w-full gap-2 mt-4">
          <div className="flex items-center justify-between">
            <label htmlFor="" className="text-sm font-medium text-main-grey">
              Item Details
            </label>
            <div
              className="flex items-center gap-1 cursor-pointer"
              onClick={() => {
                goToStep(ADD_ITEM_DETAILS);
              }}
            >
              <img src={edit} alt="Edit Icon" className="size-4" />
              <span className="text-xs font-normal cursor-pointer !text-secondary">
                Edit
              </span>
            </div>
          </div>

          <div className="p-3 space-y-4 text-sm bg-white rounded-lg cursor-pointer">
            <div className="flex items-center w-full">
              <p className="w-4/12 text-gray-10">Container Type</p>
              <p className="font-medium text-main-dark">{containerType}</p>
            </div>
            <div className="flex items-center w-full">
              <p className="w-4/12 text-gray-10">Load Type</p>
              <p className="font-medium text-main-dark">{loadType}</p>
            </div>
            <div className="flex items-center w-full">
              <p className="w-4/12 text-gray-10">Package Type</p>
              <p className="font-medium text-main-dark">{packageType}</p>
            </div>
            <div className="flex items-center w-full">
              <p className="w-4/12 text-gray-10">Total weight</p>
              <p className="font-medium text-main-dark">{weight} kg</p>
            </div>
            <div className="flex items-center w-full">
              <p className="w-4/12 text-gray-10">Total Quantity</p>
              <p className="font-medium text-main-dark">{quantity}</p>
            </div>
            <div className="flex items-center w-full">
              <p className="w-4/12 text-gray-10">Package Dimensions</p>
              <p className="font-medium text-main-dark">
                {length} cm x {width} cm x {height} cm
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col w-full gap-2 mt-4">
          <div className="flex items-center justify-between">
            <label htmlFor="" className="text-sm font-medium text-main-grey">
              Shipping Details
            </label>
            <div
              className="flex items-center gap-1 cursor-pointer"
              onClick={() => {
                goToStep(ADD_SHIPMENT_DETAILS);
              }}
            >
              <img src={edit} alt="Edit Icon" className="size-4" />
              <span className="text-xs font-normal cursor-pointer !text-secondary">
                Edit
              </span>
            </div>
          </div>
          <div className="text-xs bg-white rounded-lg">
            <div className="p-3">
              {shippingLegs.map((item, index) => (
                <Fragment key={index}>
                  <div className="flex space-x-4">
                    <div className="flex flex-col items-center">
                      <div className="flex items-center justify-center w-6 h-6 bg-gray-300 rounded-full">
                        {item.type.toLowerCase() === "truck" ? (
                          <img
                            src={truck}
                            alt="Truck Logo"
                            className="w-5 h-3"
                          />
                        ) : (
                          <img
                            src={ship}
                            alt="Truck Logo"
                            className="w-4 h-3"
                          />
                        )}
                      </div>
                      {/* {index !== shippingLegs.length - 1 && ( */}
                      <div className="w-[2px] h-8 bg-gray-300"></div>
                      {/* )} */}
                    </div>

                    <div className="pb-2">
                      <p className="text-sm text-gray-500">
                        {item.type.toLowerCase() === "truck"
                          ? "Pick-up from"
                          : "Departure Port"}
                      </p>
                      <p className="text-xs font-semibold text-gray-500">
                        {item?.departurePort?.address}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-4">
                    <div className="flex flex-col items-center">
                      <div className="flex items-center justify-center w-6 h-6 bg-gray-300 rounded-full">
                        {item.type.toLowerCase() === "truck" ? (
                          <img
                            src={truck}
                            alt="Truck Logo"
                            className="w-5 h-3"
                          />
                        ) : (
                          <img
                            src={ship}
                            alt="Truck Logo"
                            className="w-4 h-3"
                          />
                        )}
                      </div>
                      {index !== shippingLegs.length - 1 && (
                        <div className="w-[2px] h-8 bg-gray-300"></div>
                      )}
                    </div>

                    <div className="pb-2">
                      <p className="text-sm text-gray-500">
                        {item.type.toLowerCase() === "truck"
                          ? "Drop-off Point"
                          : "Destination Port"}
                      </p>
                      <p className="text-xs font-semibold text-gray-500">
                        {item?.destinationPort?.address}
                      </p>
                    </div>
                  </div>
                </Fragment>
              ))}
            </div>

            {/* Shipping Line Section */}
            <div className="grid grid-cols-3 gap-1 p-3 mt-2 border-t">
              <div className="flex justify-start gap-1 ">
                <p className="text-xs text-gray-500 flex justify-start gap-1 ">
                  {shippingLines?.length > 1 ? "Shipping Lines" : "Shipping Line"}
                </p>
              </div>
              <div className="flex justify-start">
                <p className="text-lg text-left font-semibold text-gray-900 capitalize">
                  {shippingLines?.join(", ")}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col w-full gap-2 mt-4">
          <div className="flex items-center justify-between">
            <label htmlFor="" className="text-sm font-medium text-main-grey">
              Payment Details
            </label>
            <div
              className="flex items-center gap-1 cursor-pointer"
              onClick={() => {
                goToStep(ADD_PAYMENT_INFO);
              }}
            >
              <img src={edit} alt="Edit Icon" className="size-4" />
              <span className="text-xs font-normal cursor-pointer !text-secondary">
                Edit
              </span>
            </div>
          </div>
          <div className="p-3 space-y-4 bg-white rounded-lg cursor-pointer">
            {paymentDetails.map((detail, index) => (
              <div key={index} className="grid grid-cols-3 gap-1">
                <div className="flex justify-start gap-1 ">
                  <p
                    className={`text-sm text-gray-10 ${!detail.isBold ? "mr-2" : ""
                      }`}
                  >
                    {detail.title}
                  </p>
                </div>
                <div className="flex justify-start">
                  <p
                    className={` ${detail.isBold && "font-medium"
                      } text-sm text-main-dark`}
                  >
                    {detail.fee}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col w-full gap-2 mt-4">
          <div className="flex items-center justify-between">
            <label htmlFor="" className="text-sm font-medium text-main-grey">
              Shippers
            </label>
            <div
              className="flex items-center gap-1 cursor-pointer"
              onClick={() => {
                goToStep(ADD_SHIPMENT_DETAILS);
              }}
            >
              <img src={edit} alt="Edit Icon" className="size-4" />
              <span className="text-xs font-normal cursor-pointer !text-secondary">
                Edit
              </span>
            </div>
          </div>
          <div className="w-full p-2 overflow-auto bg-white rounded-lg border-b-gray-50 text-s">
            {Object.keys(groupedShippers)?.map((k, i) => {
              return (
                <Fragment key={i}>
                  <p className="text-sm text-[#475467] uppercase">
                    {k?.charAt(0)}
                  </p>
                  <div className="w-full mb-2">
                    <div className="first:rounded-t-[10px] cursor-pointer last:rounded-b-[10px] bg-white p-[6px] flex">
                      <div className="uppercase w-[50px] h-[50px] mr-2 rounded-full bg-[#f6f6f6] flex items-center justify-center">
                        {getInitials(groupedShippers[k]?.name, 2)}
                      </div>
                      <div className="flex items-center justify-between w-full">
                        <div>
                          <p className="text-[#101828] text-[14px]">
                            {capitalizeFirstLetter(
                              groupedShippers[k]?.name
                            )}
                          </p>
                          <p className="text-[#667085] font-light text-xs">
                            {groupedShippers[k]?.businessName} •{" "}
                            {groupedShippers[k]?.businessNature ||
                              groupedShippers[k]?.email}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Fragment>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col w-full gap-2 mt-4">
          <div className="flex items-center justify-between">
            <label htmlFor="" className="text-sm font-medium text-main-grey">
              Receiver
            </label>
            <div
              className="flex items-center gap-1 cursor-pointer"
              onClick={() => {
                goToStep(ADD_RECEIVER_INFO);
              }}
            >
              <img src={edit} alt="Edit Icon" className="size-4" />
              <span className="text-xs font-normal cursor-pointer !text-secondary">
                Edit
              </span>
            </div>
          </div>
          <div className="w-full p-2 overflow-auto bg-white rounded-lg border-b-gray-50 text-s">
            <Fragment>
              <p className="text-sm text-[#475467] uppercase">
                {isReceiver
                  ? isAgent.name?.at(0)?.toUpperCase()
                  : selectedSupplier?.businessName?.at(0)?.toUpperCase() ||
                  selectedSupplier?.name?.at(0)?.toUpperCase()}
              </p>
              <div className="w-full mb-2">
                <div className="first:rounded-t-[10px] cursor-pointer last:rounded-b-[10px] bg-white p-[6px] flex">
                  <div className="uppercase w-[50px] h-[50px] mr-2 rounded-full bg-[#f6f6f6] flex items-center justify-center">
                    {getInitials(
                      isReceiver
                        ? isAgent.name
                        : selectedSupplier?.businessName ||
                        selectedSupplier?.name?.toUpperCase(),
                      2
                    )}
                  </div>
                  <div className="flex items-center justify-between w-full">
                    <div>
                      <p className="text-[#101828] text-[14px]">
                        {capitalizeFirstLetter(
                          isReceiver
                            ? isAgent?.business?.name
                            : selectedSupplier?.businessName ||
                            selectedSupplier?.name
                        )}
                      </p>
                      <p className="text-[#667085] font-light text-xs">
                        {isReceiver
                          ? isAgent?.businessName + " • " + isAgent?.email
                          : selectedSupplier?.businessName ||
                          selectedSupplier?.name +
                          " • " +
                          selectedSupplier?.email}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Fragment>
          </div>
        </div>

        {firstFileDocument || shippersDocumentsList.length ? (
          <div className="flex flex-col w-full gap-2 mt-4">
            <div className="flex items-center justify-between">
              <label htmlFor="" className="text-sm font-medium text-main-grey">
                Documents
              </label>
              <div
                className="flex items-center gap-1 cursor-pointer"
                onClick={() => {
                  goToStep(ADD_ITEM_DETAILS);
                }}
              >
                <img src={edit} alt="Edit Icon" className="size-4" />
                <span className="text-xs font-normal cursor-pointer !text-secondary">
                  Edit
                </span>
              </div>
            </div>

            <div className="p-3 space-y-4 bg-white rounded-lg cursor-pointer">
              {firstFileDocument ? (
                <div className="grid grid-cols-3 gap-1">
                  <div className="flex justify-start gap-1 ">
                    <p className="text-sm text-gray-10">Purchase Order(s)</p>
                  </div>
                  <div className="flex flex-col justify-start">
                    <p className="text-sm font-light text-main-dark">
                      {firstFileDocument?.name +
                        "." +
                        firstFileDocument?.url?.split(".").at(-1)}
                    </p>
                  </div>
                </div>
              ) : null}

              {shippersDocumentsList?.length > 0
                ? shippersDocumentsList?.map((item, index) => {
                  return (
                    <div key={index} className="grid grid-cols-3 gap-1">
                      <div className="flex justify-start gap-1 ">
                        <p className="text-sm text-gray-10">
                          Shipping Doc-{index + 1}
                        </p>
                      </div>
                      <div className="flex flex-col justify-start">
                        <p className="text-sm font-light text-main-dark">
                          {item?.name +
                            "." +
                            item?.url?.split(".").at(-1)}
                        </p>
                      </div>
                    </div>
                  );
                })
                : null}
            </div>
          </div>
        ) : null}

        <div className="flex items-center justify-end mt-10">
          <div className="flex">
            <button
              onClick={() => goToStep(ADD_RECEIVER_INFO)}
              type="button"
              className={`flex px-[24px] py-[12px] h-[40px] mr-2 text-[16px] rounded-[8px] items-center justify-center border-none outline-none text-[#1D2939]`}
            >
              Back
            </button>
            <button
              className={`flex px-[24px] py-[12px] h-[40px] text-[16px] rounded-[8px] items-center justify-center border-none outline-none text-white bg-[#6F00FF]
                            font-normal disabled:bg-secondary/30`}
              type="button"
              onClick={() => submitPaymentDetails(true)}
            >
              {isLoading ? (
                <Bars
                  color="#fff"
                  height={23}
                  width={23}
                  wrapperClass="w-max mx-auto"
                />
              ) : isEditing ? (
                "Save Changes"
              ) : (
                "Book Shipment"
              )}
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default ShipmentSummary;
